import AssetURLValidator from './utils/asset-url-validator'
import FileExtensionValidator from './utils/file-extension-validator'

export default class AssetCollection {
  private extensions = {
    sound: ['.mp3', '.mp4', '.m4a'],
    lottie: ['.json'],
    model3d: ['.obj'],
    image: ['.jpg', '.jpeg', '.png'],
    texture2d: ['.jpg', '.jpeg', '.png'],
  }
  private maxSizeInByte = 1000000 // 1 MB
  private soundAssets: GenericAsset
  private lottieAssets: GenericAsset
  private model3dAssets: GenericAsset
  private texture2dAssets: GenericAsset
  private imageAssets: GenericAsset

  constructor(options = { validateAssetUrl: false }) {
    this.soundAssets = new GenericAsset(
      this.extensions.sound,
      this.maxSizeInByte,
      options.validateAssetUrl
    )
    this.lottieAssets = new GenericAsset(
      this.extensions.lottie,
      this.maxSizeInByte,
      options.validateAssetUrl
    )
    this.model3dAssets = new GenericAsset(
      this.extensions.model3d,
      this.maxSizeInByte,
      options.validateAssetUrl
    )
    this.texture2dAssets = new GenericAsset(
      this.extensions.texture2d,
      this.maxSizeInByte,
      options.validateAssetUrl
    )

    this.imageAssets = new GenericAsset(
      this.extensions.image,
      this.maxSizeInByte,
      options.validateAssetUrl
    )
  }

  public async add(payload: AssetPayload): Promise<AssetPayload> {
    let addPromises: Promise<boolean>[] = []
    if (payload.sound) {
      addPromises = addPromises.concat(
        payload.sound.map((s) => {
          return this.soundAssets.add(s)
        })
      )
    }
    if (payload.lottie) {
      addPromises = addPromises.concat(
        payload.lottie.map((s) => {
          return this.lottieAssets.add(s)
        })
      )
    }
    if (payload.model3d) {
      addPromises = addPromises.concat(
        payload.model3d.map((s) => {
          return this.model3dAssets.add(s)
        })
      )
    }
    if (payload.texture2d) {
      addPromises = addPromises.concat(
        payload.texture2d.map((s) => {
          return this.texture2dAssets.add(s)
        })
      )
    }
    if (payload.image) {
      addPromises = addPromises.concat(
        payload.image.map((s) => {
          return this.imageAssets.add(s)
        })
      )
    }

    await Promise.all(addPromises)
    return {
      sound: this.soundAssets.get(),
      lottie: this.lottieAssets.get(),
      model3d: this.model3dAssets.get(),
      texture2d: this.texture2dAssets.get(),
      image: this.imageAssets.get(),
    }
  }

  public sound() {
    return this.soundAssets
  }

  public lottie() {
    return this.lottieAssets
  }

  public model3d() {
    return this.model3dAssets
  }

  public texture2d() {
    return this.texture2dAssets
  }

  public image() {
    return this.imageAssets
  }

  public hasSoundExists(id: string) {
    return this.soundAssets.exists(id)
  }

  public hasLottieExists(id: string) {
    return this.lottieAssets.exists(id)
  }

  public hasImageExists(id: string) {
    return this.imageAssets.exists(id)
  }

  public hasTexture2dExists(id: string) {
    return this.texture2dAssets.exists(id)
  }
}

export class GenericAsset {
  protected extensions: string[] = []
  protected maxSizeInByte: number
  private items: AssetItem[] = []
  private uniqueId: string[] = []
  private extensionValidator: FileExtensionValidator
  private validateAssetUrl = false
  constructor(
    extensions: string[],
    maxSizeInByte: number,
    validateAssetUrl = false
  ) {
    this.extensionValidator = new FileExtensionValidator(extensions)
    this.maxSizeInByte = maxSizeInByte
    this.validateAssetUrl = validateAssetUrl
  }

  public async add(item: AssetItem): Promise<boolean> {
    const url = encodeURI(item.url)
    if (item.id === null || item.id === '') {
      return false
    }
    if (this.uniqueId.includes(item.id)) {
      return false
    }
    // Reserve the ID first because URL checking can cause a delay
    this.uniqueId.push(item.id)
    if (!this.extensionValidator.validate(url)) {
      return false
    }
    if (this.validateAssetUrl) {
      const validationResult = await AssetURLValidator.validate(url)
      const assetNotExistOrFileIsTooLarge =
        !validationResult.exists ||
        validationResult.contentLength === null ||
        parseInt(validationResult.contentLength) > this.maxSizeInByte
      if (assetNotExistOrFileIsTooLarge) {
        return false
      }
    }
    const _item = {
      ...item,
      url,
    }
    return this.items.push(_item) > 0
  }

  public exists(id: string) {
    return this.uniqueId.includes(id)
  }

  public get() {
    return this.items
  }
}
